<template>
  <div v-if="open" class="wazzup-overlay" @click.self="$emit('close')">
    <section
      class="wazzup-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wazzup-title"
    >
      <header class="wazzup-dialog-head">
        <div>
          <h2 id="wazzup-title">Wazzup</h2>
          <p v-if="config.connected">Подключен к Панели активности</p>
          <p v-else>Учет сообщений напрямую из каналов Wazzup</p>
        </div>
        <button
          type="button"
          class="wazzup-close"
          aria-label="Закрыть"
          @click="$emit('close')"
        >
          ×
        </button>
      </header>

      <div v-if="loading" class="wazzup-state" aria-live="polite">
        <span class="wazzup-spinner"></span>
        Загружаем подключение и каналы
      </div>

      <div v-else-if="loadError" class="wazzup-state wazzup-state-error">
        <strong>Не удалось открыть настройки Wazzup</strong>
        <span>{{ loadError }}</span>
        <button type="button" class="wazzup-secondary" @click="loadConfig">
          Повторить
        </button>
      </div>

      <template v-else>
        <div v-if="!config.connected || changingKey" class="wazzup-connect">
          <label for="wazzup-api-key">API-ключ Wazzup</label>
          <div class="wazzup-key-row">
            <input
              id="wazzup-api-key"
              v-model.trim="apiKey"
              type="password"
              autocomplete="off"
              placeholder="Вставьте API-ключ"
              :disabled="connecting"
              @keyup.enter="connect"
            />
            <button
              type="button"
              class="wazzup-primary"
              :disabled="connecting || apiKey.length < 16"
              @click="connect"
            >
              {{ connecting ? "Подключаем..." : "Подключить" }}
            </button>
          </div>
          <p class="wazzup-hint">
            В Wazzup откройте <strong>Интеграции с CRM</strong>, добавьте
            интеграцию <strong>API</strong> и скопируйте ключ на вкладке
            <strong>Еще</strong>.
            <a
              href="https://wazzup24.com/help/api-en/authorization/"
              target="_blank"
              rel="noopener noreferrer"
            >Как получить ключ</a>
          </p>
          <button
            v-if="config.connected"
            type="button"
            class="wazzup-link-button"
            @click="cancelKeyChange"
          >
            Отмена
          </button>
        </div>

        <template v-else>
          <div class="wazzup-toolbar">
            <div class="wazzup-status">
              <span class="wazzup-status-dot"></span>
              {{ activeChannelsCount }} из {{ config.channels.length }} каналов активны
            </div>
            <button
              type="button"
              class="wazzup-link-button"
              @click="changingKey = true"
            >
              Сменить API-ключ
            </button>
          </div>

          <div class="wazzup-history-note">
            <strong>История:</strong> новые сообщения поступают после подключения.
            Прошлые сообщения можно учитывать из CRM.
          </div>

          <div v-if="!config.channels.length" class="wazzup-state">
            <strong>В Wazzup пока нет каналов</strong>
            <span>Добавьте канал в Wazzup, затем смените API-ключ или откройте настройки заново.</span>
          </div>

          <div v-else class="wazzup-mapping-wrap">
            <div class="wazzup-mapping-head">
              <span>Сотрудник CRM</span>
              <span>Каналы Wazzup</span>
              <span>Учитывать сообщения</span>
            </div>

            <div
              v-for="row in rows"
              :key="String(row.userId)"
              class="wazzup-mapping-row"
            >
              <div class="wazzup-user">
                <strong>{{ row.userName }}</strong>
                <span>{{ row.departmentName }}</span>
              </div>
              <div
                class="wazzup-channel-multiselect"
                :class="{ open: openChannelMenuUserId === String(row.userId) }"
              >
                <button
                  type="button"
                  class="wazzup-channel-trigger"
                  :aria-expanded="openChannelMenuUserId === String(row.userId)"
                  @click.stop="toggleChannelMenu(row.userId)"
                >
                  <span>{{ channelSummary(row) }}</span>
                  <span class="wazzup-channel-chevron" aria-hidden="true">⌄</span>
                </button>
                <div
                  v-if="openChannelMenuUserId === String(row.userId)"
                  class="wazzup-channel-menu"
                  @click.stop
                >
                  <label
                    v-for="channel in config.channels"
                    :key="channel.id"
                    :class="{
                      selected: row.channelIds.includes(channel.id),
                      disabled: isChannelAssignedToAnother(channel.id, row.userId),
                    }"
                  >
                    <input
                      type="checkbox"
                      :checked="row.channelIds.includes(channel.id)"
                      :disabled="isChannelAssignedToAnother(channel.id, row.userId)"
                      @change="toggleChannel(row, channel.id)"
                    />
                    <span>
                      <strong>{{ channelLabel(channel) }}</strong>
                      <small v-if="isChannelAssignedToAnother(channel.id, row.userId)">
                        Уже назначен: {{ assignedUserName(channel.id) }}
                      </small>
                    </span>
                  </label>
                </div>
              </div>
              <div class="wazzup-sources" :class="{ disabled: !row.channelIds.length }">
                <label>
                  <input
                    v-model="row.includeCrm"
                    type="checkbox"
                    :disabled="!row.channelIds.length"
                  />
                  <span>CRM</span>
                </label>
                <label>
                  <input
                    v-model="row.includeWazzup"
                    type="checkbox"
                    :disabled="!row.channelIds.length"
                  />
                  <span>Wazzup</span>
                </label>
              </div>
            </div>
          </div>

          <p v-if="formError" class="wazzup-form-error" role="alert">
            {{ formError }}
          </p>
          <p v-if="savedMessage" class="wazzup-form-success" role="status">
            {{ savedMessage }}
          </p>

          <footer class="wazzup-actions">
            <button type="button" class="wazzup-secondary" @click="$emit('close')">
              Закрыть
            </button>
            <button
              type="button"
              class="wazzup-primary"
              :disabled="saving || !config.channels.length"
              @click="save"
            >
              {{ saving ? "Сохраняем..." : "Сохранить сопоставления" }}
            </button>
          </footer>
        </template>

        <p v-if="connectError" class="wazzup-form-error" role="alert">
          {{ connectError }}
        </p>
      </template>
    </section>
  </div>
</template>

<script>
import activityApi from "../services/activityApi";

const emptyConfig = () => ({
  connected: false,
  channels: [],
  mappings: [],
});

export default {
  name: "WazzupSettingsModal",
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    users: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close", "saved"],
  data() {
    return {
      loading: false,
      connecting: false,
      saving: false,
      changingKey: false,
      loadError: "",
      connectError: "",
      formError: "",
      savedMessage: "",
      apiKey: "",
      config: emptyConfig(),
      rows: [],
      openChannelMenuUserId: "",
    };
  },
  computed: {
    activeChannelsCount() {
      return this.config.channels.filter((channel) => channel.state === "active").length;
    },
  },
  watch: {
    open(value) {
      if (value) {
        this.loadConfig();
      } else {
        this.openChannelMenuUserId = "";
      }
    },
    users() {
      if (this.open && !this.loading) {
        this.buildRows();
      }
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeydown);
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("click", this.handleDocumentClick);
  },
  methods: {
    handleKeydown(event) {
      if (this.open && event.key === "Escape") {
        if (this.openChannelMenuUserId) {
          this.openChannelMenuUserId = "";
          return;
        }

        this.$emit("close");
      }
    },
    handleDocumentClick(event) {
      if (
        this.openChannelMenuUserId &&
        !event.target.closest(".wazzup-channel-multiselect")
      ) {
        this.openChannelMenuUserId = "";
      }
    },
    async loadConfig() {
      this.loading = true;
      this.loadError = "";
      this.connectError = "";
      this.savedMessage = "";

      try {
        this.config = await activityApi.getWazzupConfig({ force: true });
        this.buildRows();
      } catch (error) {
        this.loadError = error.message || "Неизвестная ошибка";
      } finally {
        this.loading = false;
      }
    },
    buildRows() {
      const mappings = (this.config.mappings || []).reduce((result, mapping) => {
        const userId = String(mapping.userId);
        const current = result[userId] || {
          channelIds: [],
          includeCrm: mapping.includeCrm !== false,
          includeWazzup: mapping.includeWazzup !== false,
        };
        const channelIds = Array.isArray(mapping.channelIds)
          ? mapping.channelIds
          : [mapping.channelId];

        current.channelIds = Array.from(
          new Set([...current.channelIds, ...channelIds.filter(Boolean).map(String)])
        );
        result[userId] = current;
        return result;
      }, {});

      this.rows = this.users.map((user) => {
        const mapping = mappings[String(user.id)] || {};

        return {
          userId: user.id,
          userName: user.name,
          departmentName: user.departmentName || "Без отдела",
          channelIds: mapping.channelIds || [],
          includeCrm: mapping.includeCrm !== false,
          includeWazzup: mapping.includeWazzup !== false,
        };
      });
    },
    async connect() {
      if (this.apiKey.length < 16 || this.connecting) {
        return;
      }

      this.connecting = true;
      this.connectError = "";

      try {
        this.config = await activityApi.connectWazzup(this.apiKey);
        this.apiKey = "";
        this.changingKey = false;
        this.buildRows();
      } catch (error) {
        this.connectError = error.message || "Не удалось подключить Wazzup";
      } finally {
        this.connecting = false;
      }
    },
    cancelKeyChange() {
      this.apiKey = "";
      this.connectError = "";
      this.changingKey = false;
    },
    channelLabel(channel) {
      const transportNames = {
        whatsapp: "WhatsApp",
        wapi: "WABA",
        instagram: "Instagram",
        tgapi: "Telegram",
        telegram: "Telegram Bot",
        vk: "VK",
        avito: "Avito",
      };
      const transport = transportNames[channel.transport] || channel.transport || "Wazzup";
      const state = channel.state === "active" ? "" : " (не активен)";
      return `${transport}: ${channel.name}${state}`;
    },
    isChannelAssignedToAnother(channelId, userId) {
      return this.rows.some(
        (row) =>
          String(row.userId) !== String(userId) && row.channelIds.includes(channelId)
      );
    },
    assignedUserName(channelId) {
      return this.rows.find((row) => row.channelIds.includes(channelId))?.userName || "";
    },
    toggleChannelMenu(userId) {
      const normalizedUserId = String(userId);
      this.openChannelMenuUserId =
        this.openChannelMenuUserId === normalizedUserId ? "" : normalizedUserId;
    },
    toggleChannel(row, channelId) {
      this.formError = "";
      this.savedMessage = "";
      const channelIndex = row.channelIds.indexOf(channelId);

      if (channelIndex === -1) {
        row.channelIds.push(channelId);
      } else {
        row.channelIds.splice(channelIndex, 1);
      }

      if (row.channelIds.length && !row.includeCrm && !row.includeWazzup) {
        row.includeCrm = true;
        row.includeWazzup = true;
      }
    },
    channelSummary(row) {
      if (!row.channelIds.length) {
        return "Выберите каналы";
      }

      if (row.channelIds.length === 1) {
        const channel = this.config.channels.find(
          (item) => item.id === row.channelIds[0]
        );
        return channel ? this.channelLabel(channel) : "1 канал";
      }

      return `Выбрано каналов: ${row.channelIds.length}`;
    },
    validate() {
      const invalid = this.rows.find(
        (row) => row.channelIds.length && !row.includeCrm && !row.includeWazzup
      );

      if (invalid) {
        this.formError = `Выберите источник сообщений для ${invalid.userName}`;
        return false;
      }

      return true;
    },
    async save() {
      if (this.saving || !this.validate()) {
        return;
      }

      this.saving = true;
      this.formError = "";
      this.savedMessage = "";

      try {
        const mappings = this.rows
          .filter((row) => row.channelIds.length)
          .map((row) => ({
            userId: row.userId,
            userName: row.userName,
            channelIds: row.channelIds,
            includeCrm: row.includeCrm,
            includeWazzup: row.includeWazzup,
          }));
        this.config = await activityApi.saveWazzupMappings(mappings);
        this.savedMessage = "Сопоставления сохранены";
        this.buildRows();
        this.$emit("saved");
      } catch (error) {
        this.formError = error.message || "Не удалось сохранить сопоставления";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
